/* eslint-disable max-statements */
import { cacheDecorator } from '@ds/swymkit/tools'
import { encodeHTMLEntities } from '@ds/swymkit/utils'
import { defineStore } from 'pinia'

import type {
  CreateConversationOptions,
  IConversation,
} from '~/entities/container/conversation/interfaces/conversation'

import { EContext } from '~/features-shared/config-manager/interfaces/contexts'
import { useContentsStoreV2 } from '~/features-shared/content/stores/v2-contents'
import { useTemporaryMarks } from '~/features-shared/conversation/composables/temporary-marks'
import { currentContext } from '~/features-shared/left-panel/composables/left-panel-manager'
import { getIdFromSubjectUri } from '~/features-shared/libs/utils/subject-uri-utils'
import { useRTInterfaceResponseHandler } from '~/features-shared/rtconv/api/rtinterface-response-handler'
import * as RTConv from '~/features-shared/rtconv/api/rtinterface-senders'

export const useConversationsAURAStore = defineStore('conversations-aura', () => {
  const storeMessages = useContentsStoreV2()

  useRTInterfaceResponseHandler()

  const { setTempMarks, setFirstMarkSet } = useTemporaryMarks()

  // #region STATES
  const threadsId = reactive<string[]>([])
  const threads = reactive<Map<string, IConversation>>(new Map())
  // #endregion

  /**
   * Fetch conversations between AURA and the current user.
   */
  async function fetchList() {
    if (threadsId.length !== 0)
      return threads.values()
    return RTConv.getConversations({
      isBot: true,
      waitForResponse: false,
    })
  }

  // #region METHODS
  /**
   * Get a conversation by its id.
   * @param id Id of the conversation.
   */
  function getConversationById(id: string) {
    return threads.get(getIdFromSubjectUri(id))
  }

  /**
   * Add the conversation to the threads map.
   * @param conversation The IConversation.
   */
  function setConversation(conversation: IConversation): void {
    if (!getConversationById(conversation.id))
      threadsId.push(conversation.id)
    threads.set(conversation.id, conversation)
  }

  /**
   * Add the first question to the corresponding conversation in the threads map.
   * @param id Id of the conversation
   * @param firstQuestion First user question.
   */
  function setConversationFirstQuestion(id: string, firstQuestion: string | undefined): void {
    const conversation = getConversationById(id)
    if (!conversation)
      return
    conversation.firstQuestion = firstQuestion ? `<p>${encodeHTMLEntities(firstQuestion)}</p>` : undefined
  }

  /**
   * Get the conversation's title.
   * @param id Id of the conversation.
   * @param defaultTitle Default title to use.
   * @returns The default title if specified or the conversation topic.
   */
  function getConversationTitle(id: string, defaultTitle?: string): string {
    const conversation = getConversationById(id)

    if (conversation?.topic)
      return conversation.topic

    return defaultTitle ?? ''
  }

  /**
   * Get the conversation last updated time.
   * @param el IConversation.
   */
  function getConversationTime(el: IConversation) {
    return (el?.updated_at && new Date(el.updated_at)
      .getTime()) || '0'
  }

  /** Is conversation unread */
  function isConversationUnread(el: IConversation): boolean {
    return el?.last_message ? !(el.mark?.message_id === el.last_message?.message_id) : false
  }
  // #endregion

  // #region GETTERS
  const threadsVisible = computed(() => [...threads.values()].filter(el => !el.is_hidden))
  // #endregion

  // #region ACTIONS
  /**
   * Create the RTConversation.
   * @param options Options to create the conversation.
   * @param options.users Conversation's members.
   * @param options.title Title of the conversation.
   * @returns A promise resolving with the IConversation.
   */
  async function doCreateConversation(options: CreateConversationOptions): Promise<IConversation> {
    return RTConv.newConversation({
      users: { logins: options.users },
      title: options.title,
      isBot: true,
      waitForResponse: true,
    })
  }

  const fetchConversation = async (id: string) => {
    const conversation_id = getIdFromSubjectUri(id)
    if (threads.has(conversation_id))
      return getConversationById(conversation_id)
    const conversations = await RTConv.getConversations({
      convIds: [Number(conversation_id)],
      isBot: true,
      waitForResponse: true,
    })
    return conversations[0]
  }

  /**
   * Mark a conversation as read.
   * @param id Id of the conversation.
   * @returns A promise resolving with the last message read or TxID.
   */
  const markAsRead = async (id: string) => {
    const conversation = getConversationById(id)

    // Return early if there's no conversation
    if (!conversation)
      return

    const lastMessageId = conversation.last_message?.message_id

    if (lastMessageId && (lastMessageId !== conversation.mark?.message_id)) {
      setTempMarks(conversation)

      // Set the temporary mark
      const tmp = conversation.mark
      try {
        return RTConv.readConversation({
          convId: Number(conversation.id),
          msgId: Number(lastMessageId),
          waitForResponse: false,
        })
      }
      catch (err) {
        conversation.mark = tmp
        // Throw the error so that it's handled as per need
        throw err
      }
    }
    setFirstMarkSet()
  }

  const updateConversationLastMessage = (
    id: string,
    last_message_id?: string,
    last_message_timestamp?: string,
  ) => {
    const conversation = getConversationById(id)
    if (!conversation)
      return
    conversation.last_message = last_message_id ? { message_id: last_message_id } : undefined
    conversation.updated_at = last_message_timestamp
    if (getIdFromSubjectUri(storeMessages.thread_selected) === id && currentContext.value === EContext.AURA)
      conversation.mark = conversation.last_message
  }

  const updateConversationLastMessageRead = (id: string, newMessageId: string): void => {
    const conversation = getConversationById(id)
    if (!conversation)
      return
    conversation.mark = { message_id: newMessageId }
  }

  /**
   * Hide the conversation from the list.
   * @param id
   * @param hide
   * @returns A promise resolving with the conversation visibility state or TxID.
   */
  async function hideConversationAI(id: string, hide = true): Promise<string> {
    const conversation = getConversationById(id)
    if (conversation)
      conversation.is_hidden = hide
    return RTConv.setConversationHidden({
      convId: Number(conversation?.id || getIdFromSubjectUri(id)),
      isHidden: hide,
      waitForResponse: false,
    })
  }

  /**
   * Set the conversation's topic.
   * @param conversationUri Id of the conversation
   * @param topic Topic of the conversation.
   * @param isBot Is updated by bot.
   * @returns A promise resolving with the new title of the conversation or TxID.
   */
  async function setTopicAI(conversationUri: string, topic?: string, isBot?: boolean): Promise<string> {
    const title = !topic ? null : topic
    return RTConv.updateConvTitle({ convId: Number(getIdFromSubjectUri(conversationUri)), title, isBot })
  }
  // #endregion

  return {
    // #region STATES
    threads,
    // #endregion

    // #region COMPUTED
    threadsVisible,
    // #endregion

    // #region METHODS
    getConversationById,
    setConversation,
    getConversationTitle,
    getConversationTime,
    isConversationUnread,
    // #endregion

    // #region ACTIONS
    doCreateConversation,
    fetchList: () => cacheDecorator('fetch-conversations-aura', fetchList, 60000),
    fetchConversation,
    markAsRead,
    updateConversationLastMessage,
    updateConversationLastMessageRead,
    hideConversationAI,
    setConversationFirstQuestion,
    setTopicAI,
    // #endregion
  }
})
